import { updateCalculations } from "../db/query/userPlan";
import type { WorkerEnv } from "../types";

export const defalutEmailPreferences = {
  welcome: true,
  ticket_updated: true,
  quota_warning: true,
  plan_expiry: true,
  policy_update: true,
  referral_reward: true,
  payment_failed: true,
  password_changed: true,
};

export const defaultFieldsMap: Record<string, any> = {
  email: "",
  password_hash: "",
  username: "",
  avatar_url: "",
  login_type: "system",
  access_token: "",
  refresh_token: "",
  access_token_expiry: null,
  reset_token: "",
  reset_token_expiry: null,
  created_at: () => new Date().toISOString(),
  updated_at: null,
  login_activities: "[]",
  last_login: null,
  company: "",
  phone: "",
  bio: "",
  website: "",
  location: "",
  position: "",
  is_deleted: false,
  invited_by: null,
  news_subscription: false,
  privacy_policy_agreed: false,
  email_preferences: JSON.stringify(defalutEmailPreferences),
};

export const sensitiveExtensions = [
  ".env",
  ".bak",
  ".example",
  ".yaml",
  ".yml",
  ".php",
  ".config",
  ".json",
  ".xml",
];

// 公开路径（无需鉴权）
export const publicPaths = [
  "/",
  "login",
  "signup",
  "signout",
  "google",
  "proxy",
  "tiktok",
  "linkedin",
  "favicon.png",
  "tiktoksc",
  "linkedinsc",
  "xsc",
  "googlesc",
  "twitter",
  "mmzy",
  "referrer",
  "resend-verification-email",
  "verify-email",
  "check-resend-limit",
  "request-password-reset",
  "reset-password",
  "quotas",
  "credits",
  "testdata",
  "addleads",
  "unsubscribe",
  "shared",
  "paddle",
  "stream",
  "add-statistics",
];

export const ignorePaths = ["shared"];

// API v2 路径前缀
export const API_V2_PREFIX = "/api/v2/";

export const R2_PREFIX = "/r2/";

export const ADMIN_API_PATH = "/api/internal/ops";

export const TOKEN_NAME = "token";

export const ADMIN_TOKEN_NAME = "__Host-sid";

// 敏感路径关键词列表
export const sensitivePatterns = [
  "env",
  "config",
  "yml",
  ".php",
  "py",
  "secret",
  "token",
  "credentials",
  "db",
  "backup",
  "dump",
  "admin",
  "shell",
  "exploit",
  "vuln",
  ".bat",
  "ini",
  "wp-",
  "wordpress",
];

// 测试路径
export const testPath = ["tiktokm0loXSdk6YaBBtAwpXOpYzvyKuatPp4u.txt", "mmzy"];

// 支付与订单统一状态
export enum OrderStatus {
  PENDING = "pending",
  COMPLETED = "completed",
  FAILED = "failed",
  CANCELLED = "cancelled",
  EXPIRED = "expired",
  REFUNDED = "refunded",
  REFUND_PENDING = "refund_pending",
  REFUNDED_FAILED = "refund_failed",
}

export enum PaymentStatus {
  PENDING = "pending",
  SUCCESS = "success",
  FAILED = "failed",
  REFUNDED = "refunded",
  REFUND_PENDING = "refund_pending",
  REFUNDED_FAILED = "refund_failed",
}

export enum RefundStatus {
  PENDING = "pending",
  SUCCESS = "approved",
  FAILED = "failed",
  PENDING_APPROVAL = "pending_approval",
}

export enum InvoiceStatus {
  ISSUED = "issued",
  PAID = "paid",
  VOIDED = "voided",
  REFUNDED = "refunded",
  PARTIALLY_REFUNDED = "partially_refunded",
  REFUND_PENDING = "refund_pending",
  REFUNDED_FAILED = "refund_failed",
}

export enum OrderType {
  SUBSCRIPTION = "subscription",
  ONETIME = "onetime",
}

export enum OrderPlanType {
  BASIC = "Basic",
  PRO = "Pro",
  ONE_TIME = "One Time",
}

export enum BillingCycle {
  MONTHLY = "monthly",
  YEARLY = "yearly",
  ONE_OFF = "one_off",
}

export enum SubscriptionStatus {
  ACTIVE = "active",
  CANCELED = "canceled",
  CANCEL_PENDING = "cancel_pending",
  RESTORE_PENDING = "restore_pending",
  PAST_DUE = "past_due",
  PAUSED = "paused",
  TRIALING = "trialing",
}

export enum OrderCreditStatus {
  ACTIVE = "active",
  REFUND_PENDING = "refund_pending",
  REFUNDED = "refunded",
  REFUNDED_FAILED = "refund_failed",
}

export enum SSE_TYPE {
  REFUND_UPDATED = "billing.refund.updated",
  SUBSCRIPTION_UPDATED = "billing.subscription.updated",
  PAYMENT_UPDATED = "billing.payment.updated",
  NOTIFY_UPDATED = "system.notify.created",
}

// 特殊邀请码配置
export const specialInviteCodes: Record<
  string,
  {
    name: string;
    description: string;
    handler?: (env: WorkerEnv, userId: string) => Promise<void>;
  }
> = {
  WELCOME2025: {
    name: "Welcome 2025",
    description: "Special welcome offer for 2025",
    handler: async (env, userId) => {
      await updateCalculations(env, userId, env.INVITE_REWARD_CALCS, "reward");
    },
  },
  WELCOME2026: {
    name: "Welcome 2026",
    description: "Special welcome offer for 2026",
    handler: async (env, userId) => {
      await updateCalculations(env, userId, env.INVITE_REWARD_CALCS, "reward");
    },
  },
};

export const currencyFormats: Record<string, string> = {
  EUR: "€",
  GBP: "£",
  USD: "$",
};

// 文件扩展名与Content-Type的映射
export const CONTENT_TYPE_MAP: Record<string, string> = {
  // 文档格式
  md: "text/markdown; charset=utf-8",
  json: "application/json",
  html: "text/html; charset=utf-8",
  htm: "text/html; charset=utf-8",
  css: "text/css",
  js: "application/javascript",
  mjs: "application/javascript",

  // 图片格式
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  svg: "image/svg+xml",
  webp: "image/webp",
};
